import React, { useMemo } from "react";
import { DonutChart } from "@mantine/charts";
import { Box, ColorSwatch, Flex, Group, Text, rem, useMantineTheme } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { CategoryCount } from "types/analyticsTypes";

interface CategoryDistributionChartProps {
  data: CategoryCount[] | null;
  style?: React.CSSProperties;
}

const CategoryDistributionChart: React.FC<CategoryDistributionChartProps> = ({ data, style }) => {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  const palette = useMemo(
    () =>
      ["blue", "teal", "orange", "grape", "cyan", "green", "indigo", "red", "yellow", "pink"].map(
        (color) => theme.colors[color][6],
      ),
    [theme],
  );

  const chartData = useMemo(
    () =>
      (data ?? []).map((item, index) => ({
        name: t(`TestType.${item.category}`, { defaultValue: item.category }),
        value: item.count,
        color: palette[index % palette.length],
      })),
    [data, t, palette],
  );

  if (!data || data.length === 0) {
    return (
      <Box my="md">
        <Text c="dimmed">{t("analytics.category.noData")}</Text>
      </Box>
    );
  }

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Group my="md" gap="xl" align="center" wrap="wrap" style={style}>
      <Flex flex={1} miw={280} justify="center">
        <DonutChart
          data={chartData}
          chartLabel={total.toLocaleString()}
          size={240}
          thickness={30}
          paddingAngle={2}
          withTooltip
          tooltipDataSource="segment"
        />
      </Flex>
      <Box flex={1} miw={240} style={{ columnWidth: rem(220) }}>
        {chartData.map((item) => (
          <Group key={item.name} gap="xs" wrap="nowrap" mb={8} style={{ breakInside: "avoid" }}>
            <ColorSwatch color={item.color} size={10} />
            <Text size="sm" w={140} truncate>
              {item.name}
            </Text>
            <Text size="sm" fw={600}>
              {item.value.toLocaleString()}
            </Text>
          </Group>
        ))}
      </Box>
    </Group>
  );
};

export default CategoryDistributionChart;
